import React, { useEffect, useReducer, useRef, useState } from "react";

import { Input, InputChangeEvent, SwitchChangeEvent } from "@progress/kendo-react-inputs";
import { getInitialState } from "@selas/state-management";
import { EntityEditor, handleChange, IAddEntityScreenProps, IEditEntityScreenProps, YesNoSwitch } from "@selas/ui-components";
import { isNullOrEmpty } from "@selas/utils";
import { useTranslation } from "react-i18next";

import Endpoint from "../../../services/api/endpoint";
import { languageReducer } from "../../../state/reducers";
import { ILanguage } from "../../../utils/types/models";

const LanguageEditor: React.FC<IAddEntityScreenProps<ILanguage> | IEditEntityScreenProps<ILanguage>> = (props: IAddEntityScreenProps<ILanguage> | IEditEntityScreenProps<ILanguage>) => {
	const { t } = useTranslation();

	const [state, dispatch] = useReducer(languageReducer, getInitialState<ILanguage>());
	const initialLanguage: ILanguage = {
		id: (props as IEditEntityScreenProps<ILanguage>).recordId || 0,
		name: "",
		active: true
	};
	const [language, setLanguage] = useState<ILanguage>(initialLanguage);
	const [dataChanged, setDataChanged] = useState<boolean>(false);
	const firstField: React.MutableRefObject<Input> = useRef();

	useEffect(() => {
		if (state.entity) {
			setLanguage(state.entity);
		}
	}, [state.entity]);

	function onChange(event: InputChangeEvent | SwitchChangeEvent): void {
		setLanguage(handleChange(language, event));
		setDataChanged(true);
	}

	function getErrorMessages(): string[] {
		const messages: string[] = [];
		if (isNullOrEmpty(language.name)) {
			messages.push(t("fill_in_required_field", { field: t("name").toLowerCase() }));
		}
		return messages;
	}

	function onActionButtonClicked(close: boolean, record: ILanguage): void {
		if (record && !close) {
			setLanguage(record);
			setDataChanged(false);
		}
		props.actionButtonClicked(close, record);
	}

	const readonly: boolean = (props as IEditEntityScreenProps<ILanguage>).readonly;

	return (
		<EntityEditor
			width="70%"
			record={language}
			endpoint={Endpoint.Languages}
			entityState={state}
			entityType={t("language")}
			dispatch={dispatch}
			dataChanged={dataChanged}
			readonly={readonly}
			recordName={language.name}
			actionButtonClicked={onActionButtonClicked}
			getErrorMessages={getErrorMessages}
			firstFieldRef={firstField}
		>
			<div className="k-form">
				<label className="k-form-field">
					<span>{t("name")} *</span>
					<Input name="name" ref={firstField} value={language.name} onChange={onChange} required disabled={readonly} />
				</label>
				{!props.hideActive && (
					<div className="k-form-field">
						<div>{t("active")}</div>
						<YesNoSwitch name="active" checked={language.active} onChange={onChange} disabled={readonly} />
					</div>
				)}
			</div>
		</EntityEditor>
	);
};

export default LanguageEditor;
